/*
  <capra-model-card>: choosing a colour dot swaps the card to that colour's
  photo, name, price and link. The dots are real links, so without this script
  each one simply opens that colour's page.

  The swap is immediate (owner, 2026-10-16): the card's other colours are fetched
  before they are chosen, at the size the card shows (the same srcset and sizes
  as its photo, so the browser picks the same file and finds it cached). With a
  mouse they load when the pointer reaches the card; on touch screens, once the
  card has been in view for a moment. Text, price and link change at once; the
  photo changes as soon as the new one is decoded, never through a blank frame.
*/
const SIZES = '(min-width: 800px) 25vw, 50vw';
// The photo's sizes differ only in width= (the same as the card's own srcset), so the dots carry one URL
const srcset = (url) => [240, 360, 480, 720].map((w) => `${url.replace(/([?&])width=\d+/, `$1width=${w}`)} ${w}w`).join(', ');

class CapraModelCard extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => this.choose(event));
    this.addEventListener('pointerenter', () => this.preload(), { once: true });
    this.addEventListener('focusin', () => this.preload(), { once: true });
    if (matchMedia('(hover: none)').matches && 'IntersectionObserver' in window) {
      let timer;
      this.observer = new IntersectionObserver(([entry]) => {
        clearTimeout(timer);
        if (entry.isIntersecting) timer = setTimeout(() => this.preload(), 600);
      }, { threshold: 0.6 });
      this.observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  preload() {
    if (this.loaded) return;
    this.loaded = new Map();
    this.observer?.disconnect();
    for (const dot of this.querySelectorAll('.dot')) {
      const image = new Image();
      image.sizes = SIZES;
      image.srcset = srcset(dot.dataset.img);
      image.src = dot.dataset.img;
      this.loaded.set(dot, image);
    }
  }

  choose(event) {
    const dot = event.target.closest('.dot');
    if (!dot || event.metaKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    this.preload();
    for (const other of this.querySelectorAll('.dot')) other.classList.toggle('is-active', other === dot);
    this.querySelector('[data-card-link]').href = dot.href;
    this.setTitle(dot.dataset.title);
    const price = this.querySelector('[data-price]');
    if (price) price.innerHTML = new DOMParser().parseFromString(dot.dataset.price, 'text/html').querySelector('[data-price]')?.innerHTML ?? price.innerHTML;
    const image = this.querySelector('[data-card-img]');
    if (!image) return;
    this.chosen = dot;
    const swap = () => {
      if (this.chosen !== dot) return; // a later choice won
      image.sizes = SIZES;
      image.srcset = srcset(dot.dataset.img);
      image.src = dot.dataset.img;
      image.alt = dot.dataset.alt;
    };
    const next = this.loaded.get(dot);
    if (next.complete) swap();
    else next.decode().then(swap, swap);
  }

  // The same markup as snippets/card-title: the name in ink, "· Colour" muted
  setTitle(text) {
    const heading = this.querySelector('[data-card-title]');
    const [name, ...colour] = text.split(' · ');
    if (!colour.length) {
      heading.textContent = text;
      return;
    }
    const first = document.createElement('span');
    first.className = 'card__name';
    first.textContent = name;
    const second = document.createElement('span');
    second.className = 'card__colour';
    second.textContent = '· ' + colour.join(' · ');
    heading.replaceChildren(first, ' ', second);
  }
}

if (!customElements.get('capra-model-card')) customElements.define('capra-model-card', CapraModelCard);
